import { IChartState, IDetailsState, IMultiViewerState, IToolbarState, IViewerState } from '../ecg-viewer-state.models';
import {
    ArrangementType,
    Cultures,
    Format,
    GuidedComparisonType,
    PaperRate,
} from '../../ecg-viewer-module/models/enums';
import { ECG_CONSTANTS } from '../../ecg-viewer-module/utils/ecg-constants';
import { CaliperMode, TimeAlignment } from 'estensa-module';
import { createReducer, on } from '@ngrx/store';
import { ecgViewerActions } from '../actions';

export const featureKey = 'ecgViewer';

export interface ECGState {
    visualComparison: boolean;
    culture: Cultures;
    multiViewerState: IMultiViewerState;
    viewerState: IViewerState;
    chartState: IChartState;
    detailsState: IDetailsState;
    toolbarState: IToolbarState;
}

export const initialState: ECGState = {
    visualComparison: false,
    culture: Cultures.en_US,
    multiViewerState: {
        studySpacing: ECG_CONSTANTS.STUDIES_SPACING,
        horizontalStudySpacing: ECG_CONSTANTS.HORIZONTAL_STUDIES_SPACING,
    },
    viewerState: {
        toolbarVisible: true,
    },
    chartState: {
        arrangement: ArrangementType.SixXTwo,
        timeAlignment: TimeAlignment.Sequential,
        paperRate: PaperRate.mm25,
        format: Format.standard,
        fiducialPointsVisible: false,
        paperOpacity: 0.2,
        isFitBaseline: false,
        traceSpacing: ECG_CONSTANTS.TRACE_SPACING,
        guidedComparison: GuidedComparisonType.TraceToTrace,
        caliperMode: CaliperMode.single,
        zoomFactor: 1,
        printAsDisplayed: false,
    },
    detailsState: {
        visible: true,
        summaryState: {
            visible: true,
        },
    },
    toolbarState: {
        customCommandState: {
            visible: false,
            text: '',
            source: '',
        },
    },
};

export const reducer = createReducer(
    initialState,
    /* not used */
    on(
        ecgViewerActions.toolbarVisibleMessage,
        (state, action): ECGState => ({
            ...state,
            viewerState: {
                ...state.viewerState,
                toolbarVisible: action.toolbarVisible,
            },
        }),
    ),

    /* not used */
    on(
        ecgViewerActions.visualComparisonMessage,
        (state, action): ECGState => ({
            ...state,
            visualComparison: action.visualComparison,
        }),
    ),
    on(
        ecgViewerActions.customCommandMessage,
        (state, action): ECGState => ({
            ...state,
            toolbarState: {
                ...state.toolbarState,
                customCommandState: action.customCommand,
            },
        }),
    ),

    on(
        ecgViewerActions.caliperModeMessage,
        (state, action): ECGState => ({
            ...state,
            chartState: {
                ...state.chartState,
                caliperMode: action.caliperMode,
            },
        }),
    ),

    on(
        ecgViewerActions.arrangementMessage,
        (state, action): ECGState => ({
            ...state,
            chartState: {
                ...state.chartState,
                arrangement: action.arrangement,
            },
        }),
    ),

    on(
        ecgViewerActions.printAsDisplayedMessage,
        (state, action): ECGState => ({
            ...state,
            chartState: {
                ...state.chartState,
                printAsDisplayed: action.printAsDisplayed,
            },
        }),
    ),

    on(
        ecgViewerActions.cultureMessage,
        (state, action): ECGState => ({
            ...state,
            culture: action.culture,
        }),
    ),
    on(
        ecgViewerActions.fiducialPointsVisibleMessage,
        (state, action): ECGState => ({
            ...state,
            chartState: {
                ...state.chartState,
                fiducialPointsVisible: action.fiducialPointsVisible,
            },
        }),
    ),
    on(
        ecgViewerActions.horizontalStudySpacingMessage,
        (state, action): ECGState => ({
            ...state,
            multiViewerState: {
                ...state.multiViewerState,
                horizontalStudySpacing: action.horizontalStudySpacing,
            },
        }),
    ),

    on(
        ecgViewerActions.studySpacingMessage,
        (state, action): ECGState => ({
            ...state,
            multiViewerState: {
                ...state.multiViewerState,
                studySpacing: action.studySpacing,
            },
        }),
    ),
    on(
        ecgViewerActions.guidedComparisonMessage,
        (state, action): ECGState => ({
            ...state,
            chartState: {
                ...state.chartState,
                traceSpacing: action.guidedComparison.traceSpacing,
                guidedComparison: action.guidedComparison.guidedComparison,
            },
            multiViewerState: {
                ...state.multiViewerState,
                studySpacing: action.guidedComparison.studySpacing,
                horizontalStudySpacing: action.guidedComparison.horizontalStudySpacing,
            },
        }),
    ),
    on(
        ecgViewerActions.traceSpacingMessage,
        (state, action): ECGState => ({
            ...state,
            chartState: {
                ...state.chartState,
                traceSpacing: action.traceSpacing,
            },
        }),
    ),

    //OK
    on(
        ecgViewerActions.paperRateMessage,
        (state, action): ECGState => ({
            ...state,
            chartState: {
                ...state.chartState,
                paperRate: action.paperRate,
            },
        }),
    ),
    //OK
    on(
        ecgViewerActions.timeAlignmentMessage,
        (state, action): ECGState => ({
            ...state,
            chartState: {
                ...state.chartState,
                timeAlignment: action.timeAlignment,
            },
        }),
    ),
    //OK
    on(
        ecgViewerActions.formatMessage,
        (state, action): ECGState => ({
            ...state,
            chartState: {
                ...state.chartState,
                format: action.format,
            },
        }),
    ),

    //NOT USED
    on(
        ecgViewerActions.baselineMessage,
        (state, action): ECGState => ({
            ...state,
            chartState: {
                ...state.chartState,
                isFitBaseline: action.isFitBaseline,
            },
        }),
    ),

    //OK
    on(
        ecgViewerActions.zoomFactorMessage,
        (state, action): ECGState => ({
            ...state,
            chartState: {
                ...state.chartState,
                zoomFactor: action.zoomFactor,
            },
        }),
    ),

    on(
        ecgViewerActions.paperOpacityMessage,
        (state, action): ECGState => ({
            ...state,
            chartState: {
                ...state.chartState,
                paperOpacity: action.paperOpacity,
            },
        }),
    ),

    on(
        ecgViewerActions.summaryVisibleMessage,
        (state, action): ECGState => ({
            ...state,
            detailsState: {
                ...state.detailsState,
                summaryState: {
                    ...state.detailsState.summaryState,
                    visible: action.summaryVisible,
                },
            },
        }),
    ),

    on(
        ecgViewerActions.detailsVisibleMessage,
        (state, action): ECGState => ({
            ...state,
            detailsState: {
                ...state.detailsState,
                visible: action.detailsVisible,
            },
        }),
    ),
);
export const fullState = (state: ECGState) => state;

export const horizontalStudySpacing = (state: ECGState) => state.multiViewerState.horizontalStudySpacing;

export const traceSpacing = (state: ECGState) => state.chartState.traceSpacing;

export const paperRate = (state: ECGState) => state.chartState.paperRate;

export const zoomFactor = (state: ECGState) => state.chartState.zoomFactor;

export const isFitBaseline = (state: ECGState) => state.chartState.isFitBaseline;

export const format = (state: ECGState) => state.chartState.format;

export const paperOpacity = (state: ECGState) => state.chartState.paperOpacity;

export const timeAlignment = (state: ECGState) => state.chartState.timeAlignment;

export const studySpacing = (state: ECGState) => state.multiViewerState.studySpacing;

export const fiducialPointsVisible = (state: ECGState) => state.chartState.fiducialPointsVisible;

export const printAsDisplayed = (state: ECGState) => state.chartState.printAsDisplayed;

export const caliperMode = (state: ECGState) => state.chartState.caliperMode;

export const summaryVisible = (state: ECGState) => state.detailsState.summaryState.visible;

export const detailsVisible = (state: ECGState) => state.detailsState.visible;

export const customCommand = (state: ECGState) => state.toolbarState.customCommandState;

export const arrangement = (state: ECGState) => state.chartState.arrangement;

export const visualComparison = (state: ECGState) => state.visualComparison;
export const culture = (state: ECGState) => state.culture;
export const multiViewerState = (state: ECGState) => state.multiViewerState;
export const viewerState = (state: ECGState) => state.viewerState;
export const chartState = (state: ECGState) => state.chartState;
export const detailsState = (state: ECGState) => state.detailsState;
export const toolbarState = (state: ECGState) => state.toolbarState;
